import { produce } from 'immer';
import { ASSIGNMENT_LABELS, ASSIGNMENTS, EXPORTS, IDB_ATTRIBUTES } from './constants';
import tags from '../../../constants/tags';

const { ORTHOSELECT, THIRD_PARTY } = ASSIGNMENTS;

export function assignmentOptions({
  assignments = Object.values(ASSIGNMENTS),
  thirdPartyName = 'Organization'
}) {
  return assignments.map(value => {
    const label = ASSIGNMENT_LABELS[value];
    return { value, label: value === THIRD_PARTY ? `${thirdPartyName}` : label };
  });
}

export function getDefaultAssignment(options) {
  if (options.indexOf(ORTHOSELECT) !== -1) {
    return ORTHOSELECT;
  }
  if (options.indexOf(THIRD_PARTY) !== -1) {
    return THIRD_PARTY;
  }
  return options[0];
}

export function getDefaultTrayDesignAssignment(preference) {
  const value =
    Array.isArray(preference.tray_design_assignment_options) &&
    preference.tray_design_assignment_options.length > 0
      ? preference.tray_design_assignment_options[
          preference.tray_design_assignment_options.length - 1
        ].toString()
      : null;

  if (
    preference.export_type !== EXPORTS.TRAYS ||
    getDefaultAssignment(preference.print_assignment_options) !== ASSIGNMENTS.IN_OFFICE
  ) {
    return null;
  }
  return value;
}

export function getDefaultPreferenceTrayDesignAssignment(preference) {
  return Array.isArray(preference.tray_design_assignment_options) &&
    preference.tray_design_assignment_options.length > 0
    ? preference.tray_design_assignment_options[0].toString()
    : null;
}

export const idbAttributeTypes = {
  [IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT]: Number,
  [IDB_ATTRIBUTES.RUSH_CASE]: Boolean,
  [IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD]: Number,
  [IDB_ATTRIBUTES.SCANNER]: Number,
  [IDB_ATTRIBUTES.ARCH_TYPE]: Number,
  [IDB_ATTRIBUTES.EXPORT_TYPE]: Number,
  [IDB_ATTRIBUTES.PRINT_ASSIGNMENT]: Number,
  [IDB_ATTRIBUTES.TRAY_DESIGN_ASSIGNMENT]: Number,
  [IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT]: Number,
  [IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE]: Number,
  [IDB_ATTRIBUTES.CUSTOM_SETUP]: Boolean
};

const appendCurrentAssignment = (options, currentValue) => {
  const currentAssignment = Number(currentValue);
  if (
    currentValue === null ||
    currentValue === undefined ||
    currentValue === '' ||
    !Number.isFinite(currentAssignment) ||
    !Array.isArray(options) ||
    options.indexOf(currentAssignment) !== -1
  ) {
    return options;
  }

  return [...options, currentAssignment];
};

export const overridePreferenceWithModificationRules = (
  preference,
  modificationRules,
  rxForm = null
) => {
  const override = {};
  if (modificationRules.submission_completed_by) {
    override.initial_case_setup_assignment_options =
      modificationRules.submission_completed_by.allowed_values;
  }

  if (modificationRules.print_assignment) {
    override.print_assignment_options = modificationRules.print_assignment.allowed_values;
  }

  const result = { ...preference, ...override };
  if (rxForm) {
    result.print_assignment_options = appendCurrentAssignment(
      result.print_assignment_options,
      rxForm.print_assignment
    );
    result.initial_case_setup_assignment_options = appendCurrentAssignment(
      result.initial_case_setup_assignment_options,
      rxForm.submission_completed_by
    );
    result.tray_design_assignment_options = appendCurrentAssignment(
      result.tray_design_assignment_options,
      rxForm.tray_design_assignment
    );
  }

  return result;
};

export const validateDicomUpload = async file => {
  const minSizeBytes = 2465792;

  if (file.size < minSizeBytes) {
    return 'The zip file should be larger than 2MB.';
  }

  return null;
};

const autoDetectStlType = (file, files) => {
  const manPattern = /^.*(mand.*|mandibular.*|mandible.*|lower.*|[_\-\s]l)\.stl$/i;

  if (manPattern.test(file.name)) {
    return tags.STL_MANDIBULAR;
  }

  const maxPattern = /^.*(max.*|maxillary.*|maxilla.*|upper.*|[_\-\s]u)\.stl$/i;

  if (maxPattern.test(file.name)) {
    return tags.STL_MAXILLARY;
  }

  return files.some(f => f.tag === tags.STL_MANDIBULAR) ? tags.STL_MAXILLARY : tags.STL_MANDIBULAR;
};

export const autoDetectStlTypes = files => {
  return produce(files || [], draft => {
    let firstDetection = '';
    for (let i = 0; i < draft.length; i += 1) {
      if (i > 1) {
        draft[i].tag = null;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (!firstDetection && draft[i].tag) {
        firstDetection = draft[i].tag;
      }

      if (!draft[i].tag) {
        if (firstDetection) {
          draft[i].tag =
            firstDetection === tags.STL_MAXILLARY ? tags.STL_MANDIBULAR : tags.STL_MAXILLARY;
        } else {
          firstDetection = draft[i].tag = autoDetectStlType(draft[i], draft);
        }
      }
    }
  });
};
